import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Encoding from 'encoding-japanese';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import NotificationModal from './NotificationModal';
import ErrorDisplay from './components/ErrorDisplay';
import { logActivity } from './lib/activityLogger';
import { useUser } from './hooks/UserContext';

const Home = ({ onBeforeUpload }) => {
  const { t, i18n } = useTranslation('home');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(t('selectFile'));
  const [result, setResult] = useState('');
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const isSubmittingRef = useRef(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [userToken, setUserToken] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [stripeEnv, setStripeEnv] = useState(null);
  const fileInputRef = useRef(null);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationModalContent, setNotificationModalContent] = useState({ title: '', message: '' });
  const { user } = useUser();

  useEffect(() => {
    document.title = "BillWave";
  }, []);

  useEffect(() => {
    setFileName(t('selectFile'));
  }, [t]);

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    if (!userToken) {
      throw new Error('No user token available. Please log in.');
    }
    const newOptions = { ...options };
    newOptions.headers = { ...newOptions.headers, Authorization: `Bearer ${userToken}` };
    if (newOptions.body && typeof newOptions.body === 'object') {
      newOptions.headers['Content-Type'] = 'application/json';
      newOptions.body = JSON.stringify(newOptions.body);
    }
    const response = await fetch(url, newOptions);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Request failed with status ${response.status}`);
    }
    return response;
  }, [userToken]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          setUserToken(token);
        } catch (error) {
          console.error("Error getting user token:", error);
          setUserToken(null);
        }
      } else {
        setUserToken(null);
      }
      setAuthInitialized(true);
    });
    return () => unsubscribe();
  }, []);

  const checkApiKeyStatus = useCallback(async () => {
    if (!authInitialized || !userToken) return false;
    try {
      const response = await fetchWithAuth('/v1/stripe-api-key-status');
      const data = await response.json();
      if (!data.exists) {
        setStripeEnv(null);
        setNotificationModalContent({
          title: t('stripeApiKeyWarningTitle'),
          message: t('stripeApiKeyWarning'),
        });
        setIsNotificationModalOpen(true);
        return false;
      }
      if (data.environment) {
        setStripeEnv(data.environment);
      } else {
        setStripeEnv(null);
      }
      return true;
    } catch (error) {
      console.error('API key status error:', error.message);
      setErrors([{ message: 'APIキーのステータス確認中にエラーが発生しました。' }]);
      return false;
    }
  }, [authInitialized, userToken, fetchWithAuth, t]);

  useEffect(() => {
    if (authInitialized && userToken) {
      checkApiKeyStatus();
    }
  }, [authInitialized, userToken, checkApiKeyStatus]);

  const handleFile = (selectedFile) => {
    if (selectedFile) {
      const fName = selectedFile.name.toLowerCase();
      if (!fName.endsWith('.csv')) {
        setResult(t('invalidFileType'));
        setFile(null);
        setFileName(t('noFileSelected'));
        return;
      }
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setResult('');
      setErrors([]);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoading || isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsLoading(true);

    try {
      if (!file) {
        setResult(t('selectFileError'));
        return;
      }

      const apiKeyExists = await checkApiKeyStatus();
      if (!apiKeyExists) {
        return;
      }

      setErrors([]);
      setResult(t('sendingData'));

      const buffer = await file.arrayBuffer();
      const detectedEncoding = Encoding.detect(new Uint8Array(buffer));
      const text = new TextDecoder(detectedEncoding === 'SJIS' ? 'shift-jis' : 'utf-8').decode(buffer);

      const res = await fetchWithAuth('/v1/invoice', {
          method: 'POST',
          body: { 
            csv: text, 
            language: i18n.language, 
            fileName: file.name
          },
      });

      const apiResult = await res.json();

      if (apiResult.success) {
          const { dataImport, billing } = apiResult;
          let formattedResult = '';
          formattedResult += `${t('dataImportHeader')}\n`;
          formattedResult += `${t('totalRecords', { count: dataImport.totalRecords || 0 })}\n`;
          formattedResult += `${t('successCount', { count: dataImport.successCount || 0 })}\n`;
          formattedResult += `${t('errorCount', { count: dataImport.errorCount || 0 })}\n`;
          formattedResult += `\n${t('billingHeader')}\n`;
          formattedResult += `${t('invoicesScheduled', { count: billing.scheduledCount || 0 })}\n`;
          formattedResult += `${t('billingError', { count: billing.errorCount || 0 })}\n`;
          setResult(formattedResult);
          setErrors([]);
          logActivity({
              billwaveCustomerId: user.billwaveCustomerId,
              action: 'invoice_csv_upload',
              result: 'success',
              target: { type: 'invoice_csv', name: file.name },
              actor: { uid: user.uid, email: user.email, name: user.representativeName },
              details: { dataImport, billing }
            });
      } else {
          const apiErrors = Array.isArray(apiResult.error) ? apiResult.error : [apiResult.error];
          setErrors(apiErrors);
          setResult('');
          logActivity({
              billwaveCustomerId: user.billwaveCustomerId,
              action: 'invoice_csv_upload',
              result: 'failed',
              target: { type: 'invoice_csv', name: file.name },
              actor: { uid: user.uid, email: user.email, name: user.representativeName },
              details: { error: apiErrors }
            });
      }
    } catch (error) {
        let errorDetails = { message: error.message };
        try {
            const errorData = JSON.parse(error.message);
            const apiErrors = Array.isArray(errorData.error) ? errorData.error : [errorData.error];
            const sf301Error = apiErrors.find(err => err.code === 'SF301');
            errorDetails = { error: apiErrors };

            if (sf301Error) {
                setNotificationModalContent({
                    title: t('freePlanLimitModalTitle'),
                    message: t('freePlanLimitModalMessage'),
                });
                setIsNotificationModalOpen(true);
                setResult('');
                setErrors([]);
            } else {
                setErrors(apiErrors.length > 0 ? apiErrors : [{ message: error.message }]);
                setResult('');
            }
        } catch (e) {
            setErrors([{ message: error.message }]);
            setResult('');
        }
        logActivity({
            billwaveCustomerId: user?.billwaveCustomerId,
            action: 'invoice_csv_upload',
            result: 'failed',
            target: { type: 'invoice_csv', name: file?.name },
            actor: { uid: user?.uid, email: user?.email, name: user?.representativeName },
            details: errorDetails
          });
    } finally {
        setIsLoading(false);
        isSubmittingRef.current = false;
        setFile(null);
        setFileName(t('selectFile'));
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
    }
  };

  const closeNotificationModal = () => setIsNotificationModalOpen(false);

  const dropZoneClass = isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50';

  if (!authInitialized) {
    return <div className="flex justify-center items-center h-screen"><p>{t('authLoading')}</p></div>;
  }

  if (!userToken) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <h1 className="text-2xl font-bold text-red-600 mb-4">{t('authTitle')}</h1>
              <p className="text-gray-700">{t('authDescription')}</p>
          </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 w-full max-w-4xl mx-auto my-8 shadow-lg rounded-lg">
      <div className="flex justify-between items-center mb-6 pb-2 border-b-2 border-gray-200">
        <h1 className="text-3xl font-bold text-gray-900">{t('mainTitle')}</h1>
        {stripeEnv && (
          <div className="flex items-center space-x-2 text-sm bg-gray-50 px-3 py-1.5 border rounded-md">
            <span className="font-semibold text-gray-700">Stripe環境:</span>
            <span className={`font-bold px-2 py-0.5 rounded ${stripeEnv === 'live' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {stripeEnv === 'live' ? '本番' : 'テスト'}
            </span>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
        <div
          className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg transition-colors cursor-pointer hover:bg-gray-100 ${dropZoneClass}`}
          onClick={() => fileInputRef.current.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L6.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
          <span className="text-gray-500">{fileName}</span>
          <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
        </div>
        <button type="submit" disabled={isLoading} className="bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors shadow-md disabled:bg-blue-400 disabled:cursor-not-allowed">
          {isLoading ? t('submitButtonLoading') : t('submitButton')}
        </button>
      </form>
      
      <div className="mt-8">
        <ErrorDisplay errors={errors} />
        {result && (
          <div>
            <h2 className="text-xl font-semibold mb-2 border-b-2 border-gray-200">{t('resultTitle')}</h2>
            <pre className="bg-gray-100 text-gray-900 p-4 rounded-lg overflow-x-auto text-sm whitespace-pre-wrap mt-4">{result}</pre>
          </div>
        )}
      </div>

      <NotificationModal
          isOpen={isNotificationModalOpen}
          onClose={closeNotificationModal}
          title={notificationModalContent.title}
      >
          <p>{notificationModalContent.message}</p>
      </NotificationModal>
    </div>
  );
};

export default Home;
